import { t } from 'i18next'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'

import { Delete, Share, SportsEsports, Warning } from '@mui/icons-material'
import { Box, Button, Card, CircularProgress, Grid, ListItemIcon, ListItemText, MenuItem, MenuList, Typography } from '@mui/material'

import { useGetUserProfileQuery, useGetUserSettingQuery, useUpdateUserSettingMutation } from '../../api/combined'
import { GetGameQueryParams, useGetMultipleGamesAndAnalysisQuery } from '../../api/core'
import AddArea from '../../components/AddArea/AddArea'
import { GRBanner } from '../../components/GRBanner/GRBanner'
import GRTabNavigation from '../../components/GRTabNavigation/GRTabNavigation'
import { LockedFeature } from '../../components/LockedFeature/LockedFeature'
import { RoleEnum } from '../../features/account'
import { useCompareGamesAccessCheck, useRoleCheck, useSoftLaunchAccessCheck } from '../../features/account/hooks/roleHooks'
import { useCurrentUserLanguage } from '../../features/account/hooks/useCurrentUserLanguage'
import { UserSettingKeys } from '../../features/account/types/UserSettings'
import FeatureTabView from '../../features/compare-games/components/FeatureTabView/FeatureTabView'
import PerformanceTabView from '../../features/compare-games/components/PerformanceTabView/PerformanceTabView'
import { isJSON } from '../../features/compare-games/utils/isJSON'
import { paramObj, shallowCompare } from '../../features/compare-games/utils/shallowCompare'
import { Game, GameLink } from '../../features/game'
import GameSearchDialog, { GameWithMarketIso } from '../../features/game-search/components/GameSearchDialog/GameSearchDialog'
import { GameSearchDialogConfig, GameSearchDialogTab, GameSearchDialogType } from '../../features/game-search/types/GameSearchDialogConfig'
import GameCard from '../../features/game/components/GameCard/GameCard'
import { useFollowedGamesPageGameIds } from '../../features/game/hooks/gameHooks'
import { GameAndAnalysis } from '../../features/game/types/GameAndAnalysis'
import MotivationsTabView from '../../features/motivations/components/MotivationsTabView/MotivationsTabView'
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard'
import { useDocumentTitle } from '../../hooks/useDocumentTitle'
import usePage from '../../hooks/usePage'
import { useSearchParams } from '../../hooks/useSearchParams'
import analyticsService from '../../services/AnalyticsService'
import PageService from '../../services/PageService'
import { AnalyticsProviders } from '../../types/IAnalyticsProvider'
import { LockedFeatureId } from '../../types/LockedFeature'

const maxGameCompare = 6
const minGameCompare = 2

/**
 * GameCards wrapper to show game cards when select
 */
interface GameCardsProps {
  gamesAndAnalysis?: GameAndAnalysis[]
  addMoreGameClick: () => void
  onGameRemoveClick: (game: Game, marketIso?: string) => void
  onClearAllGameClick: () => void
}
const GameCards: React.FC<GameCardsProps> = ({ gamesAndAnalysis = [], addMoreGameClick, onGameRemoveClick, onClearAllGameClick }) => {
  const copyToClipboard = useCopyToClipboard()

  return (
    <div className="FollowedGamesPage__quick-links">
      <Grid container wrap="wrap" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} rowSpacing={1}>
        <Grid item xs={12} lg={6}>
          <Typography margin={0} variant="h3">
            {t('compare:compare_multiple_games_desc', { min: minGameCompare, max: maxGameCompare })}
          </Typography>
        </Grid>
        <Grid item>
          <Box>
            {gamesAndAnalysis.length > 1 && (
              <Button sx={{ mr: 1 }} variant="contained" onClick={() => copyToClipboard()}>
                <Share sx={{ mr: 1 }} />
                {t('common:share_text')}
              </Button>
            )}
            {gamesAndAnalysis.length > 0 && (
              <Button variant="contained" onClick={onClearAllGameClick}>
                {t('select-game:clear_selected_games')}
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={2} wrap="wrap">
        {gamesAndAnalysis.map((gameAndAnalysis) => {
          const { game, analysis } = gameAndAnalysis

          return (
            <Grid item key={`${game.id}-${analysis?.marketIso}`} xs={12} md={4} sm={6}>
              <GameCard
                game={game}
                marketIso={analysis?.marketIso}
                showCountryFlag
                onCardClick={() => {
                  addMoreGameClick()
                }}
              >
                <GameCard.Actions>
                  <MenuList>
                    <GameLink style={{ color: 'inherit' }} game={game}>
                      {/* Game View action */}
                      <MenuItem>
                        <ListItemIcon>
                          <SportsEsports />
                        </ListItemIcon>
                        <ListItemText>{t('overview:game_overview')}</ListItemText>
                      </MenuItem>
                    </GameLink>

                    {/* Remove game action */}
                    <MenuItem
                      onClick={() => {
                        onGameRemoveClick(game, analysis?.marketIso)
                      }}
                    >
                      <ListItemIcon>
                        <Delete />
                      </ListItemIcon>
                      <ListItemText sx={{ textTransform: 'capitalize' }}>{t('common:remove')}</ListItemText>
                    </MenuItem>
                  </MenuList>
                </GameCard.Actions>
              </GameCard>
            </Grid>
          )
        })}

        {gamesAndAnalysis.length < maxGameCompare && (
          <Grid item xs={12} md={4} sm={6}>
            <AddArea
              label={t('common:add_new_game') as string}
              onClick={() => {
                addMoreGameClick()
              }}
            />
          </Grid>
        )}
      </Grid>
    </div>
  )
}

type CompareGameParams = {
  gamesWithMarketIso?: string
  force?: string
}

export enum CompareGameTabSelection {
  FEATURES = 'features',
  MOTIVATION = 'motivation',
  PERFORMANCE = 'performance',
}

type compareGameTab = {
  id: CompareGameTabSelection
  name: string
}

/**
 * CompareGamesPage Compage Game View
 */
interface CompareGamesPageProps {}
const CompareGamesPage: React.FC<CompareGamesPageProps> = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const userLanguage = useCurrentUserLanguage()
  const hasCompareGamesAccess = useCompareGamesAccessCheck()
  const hasAccessToSoftLaunch = useSoftLaunchAccessCheck()

  usePage(PageService.getPageWithId('compare-games'), 'Visited Game Comparison')
  useDocumentTitle(t('sidebar:game_comparison'))

  const navigate = useNavigate()
  const [gameSearchModalOpen, setGameSearchModalOpen] = useState(false)
  const { subpage } = useParams<{ subpage: CompareGameTabSelection }>()

  const { parsedParams, setSearchParams } = useSearchParams<CompareGameParams, CompareGameParams>()

  const [parsedGamesWithMarketIso, setParsedGamesWithMarketIso] = useState<GameWithMarketIso>({})
  const hasCleanedSharedUrl = useRef(false)
  useEffect(() => {
    if (!!parsedParams.gamesWithMarketIso && isJSON(parsedParams.gamesWithMarketIso)) {
      setParsedGamesWithMarketIso(JSON.parse(parsedParams.gamesWithMarketIso))
    }
  }, [parsedParams.gamesWithMarketIso])

  const queryParams = useMemo(() => {
    return Object.keys(parsedGamesWithMarketIso).reduce<GetGameQueryParams[]>((result, gameId) => {
      const queryParams = parsedGamesWithMarketIso[gameId].map((marketIso) => ({ id: gameId, marketIso, tags: true, userLanguage }))
      return [...result, ...queryParams]
    }, [])
  }, [parsedGamesWithMarketIso, userLanguage])

  const {
    data: gameAndAnalysisData,
    currentData: gameAndAnalysisCurrentData,
    isLoading: gameAndAnalysisDataLoading,
  } = useGetMultipleGamesAndAnalysisQuery({ queryParams })
  const allGamesUnlocked = useRoleCheck(RoleEnum.all_games_unlocked)
  const { followedGamesIds, isLoading: followedGamesIdsLoading } = useFollowedGamesPageGameIds()
  const { data: currentUser, isLoading: currentUserLoading } = useGetUserProfileQuery()

  // The cleanup effect below filters the URL based on which games the user has access to.
  // Access is derived from `currentUser` (roles + own games), `followedGamesIds`, and
  // `gameAndAnalysisData`. Several races can leave us with empty/stale access data while
  // the URL already contains games, so we must be conservative about declaring the data
  // "ready" before letting cleanup run:
  //
  //  1. `gameAndAnalysisDataLoading`, `followedGamesIdsLoading`, `currentUserLoading` cover
  //     the obvious first-load case.
  //  2. RTK Query's `data` field falls back to the previous render's value when query args
  //     change, so right after `queryParams` flips from `[]` (the empty initial state) to a
  //     populated list (e.g. when the user-setting hydration effect just pushed the saved
  //     games into the URL on navigate-back-after-reload), `gameAndAnalysisData` briefly
  //     returns the stale `[]` and `isLoading` is still false. `currentData` is the
  //     un-fallback'd value for the *current* args, so it stays `undefined` until the new
  //     query resolves — gate cleanup on that to close the race window. (GR-2880)
  const isCurrentGameDataReady = queryParams.length === 0 || gameAndAnalysisCurrentData !== undefined
  const isAccessDataLoading = gameAndAnalysisDataLoading || !isCurrentGameDataReady || followedGamesIdsLoading || currentUserLoading

  const isOwnGame = useCallback(
    (game?: Game) => {
      if (!currentUser?.organization?.id) return false
      return game?.owner === currentUser.organization.id
    },
    [currentUser?.organization?.id]
  )

  const filteredGameAndAnalysisData = useMemo(() => {
    if (allGamesUnlocked) return gameAndAnalysisData

    return gameAndAnalysisData?.filter(({ game }) => followedGamesIds.includes(game.id) || isOwnGame(game))
  }, [allGamesUnlocked, gameAndAnalysisData, followedGamesIds, isOwnGame])

  const filteredGamesWithMarketIso = useMemo(() => {
    // While any access-related data is still loading, don't filter yet (show count from URL).
    // Filtering before `followedGamesIds`/`currentUser` resolve would drop all games and
    // strip `gamesWithMarketIso` from the URL on reload (GR-2880).
    if (isAccessDataLoading) {
      return parsedGamesWithMarketIso
    }

    // After loading completes, filter based on what actually loaded
    // Get IDs of games that successfully loaded from the API
    const loadedGameIds = filteredGameAndAnalysisData?.map(({ game }) => game.id) || []

    // Only include games that were successfully loaded from the API
    const filtered: GameWithMarketIso = {}
    Object.keys(parsedGamesWithMarketIso).forEach((gameId) => {
      if (loadedGameIds.includes(gameId)) {
        filtered[gameId] = parsedGamesWithMarketIso[gameId]
      }
    })

    return filtered
  }, [parsedGamesWithMarketIso, filteredGameAndAnalysisData, isAccessDataLoading])

  useEffect(() => {
    if (isAccessDataLoading || hasCleanedSharedUrl.current) return

    // The `parsedGamesWithMarketIso` state is hydrated from the URL by an effect that runs
    // after the first render; until that has happened we cannot trust an empty `parsedKeys`
    // because the URL may still contain games we just have not parsed yet. Parse the URL
    // value once (with try/catch) so we don't validate-then-reparse, then bail out if the
    // URL has games but local state is still empty, so we don't strip the URL on reload
    // while access-data has briefly settled. (GR-2880)
    let urlGameCount = 0
    if (parsedParams.gamesWithMarketIso) {
      try {
        const parsedFromUrl = JSON.parse(parsedParams.gamesWithMarketIso)
        if (parsedFromUrl && typeof parsedFromUrl === 'object') {
          urlGameCount = Object.keys(parsedFromUrl).length
        }
      } catch {
        // Malformed JSON in URL — treat as no games and proceed with cleanup as usual.
        urlGameCount = 0
      }
    }
    const parsedKeys = Object.keys(parsedGamesWithMarketIso)
    if (urlGameCount > 0 && parsedKeys.length === 0) return

    const filteredKeys = Object.keys(filteredGamesWithMarketIso)
    if (parsedKeys.length > 0 && filteredKeys.length !== parsedKeys.length) {
      hasCleanedSharedUrl.current = true
      setSearchParams((currentParams) => ({
        ...currentParams,
        gamesWithMarketIso: filteredKeys.length ? JSON.stringify(filteredGamesWithMarketIso) : undefined,
      }))
    } else if (parsedKeys.length > 0) {
      hasCleanedSharedUrl.current = true
    }
  }, [isAccessDataLoading, filteredGamesWithMarketIso, parsedGamesWithMarketIso, parsedParams.gamesWithMarketIso, setSearchParams])

  const tabs: compareGameTab[] = useMemo(
    () => [
      {
        id: CompareGameTabSelection.FEATURES,
        name: t('common:features'),
      },
      {
        id: CompareGameTabSelection.MOTIVATION,
        name: t('motivations:motivations'),
      },
      {
        id: CompareGameTabSelection.PERFORMANCE,
        name: t('common:performance'),
      },
    ],
    [t]
  )

  const defaultTabView = useCallback(() => {
    const subPageTab = subpage ? tabs.map((tab) => tab.id).indexOf(subpage) : 0
    return subPageTab !== -1 ? subPageTab : 0
  }, [subpage, tabs])

  const [tabView, setTabView] = useState<number>(defaultTabView)
  const [saveUserSetting] = useUpdateUserSettingMutation()

  const { data: gameListSetting, isLoading: gameListSettingLoading } = useGetUserSettingQuery(UserSettingKeys.compareGamesList)

  const [isCleaningSelectedGames, setIsCleaningSelectedGames] = useState(false)
  const onClearAllGameClick = async () => {
    try {
      setIsCleaningSelectedGames(true)
      setSearchParams((currentParams) => ({ ...currentParams, gamesWithMarketIso: undefined }))
      navigate({ search: undefined }, { replace: true })
      setParsedGamesWithMarketIso({})
      await saveUserSetting({ settingKey: UserSettingKeys.compareGamesList, value: {} })

      setIsCleaningSelectedGames(false)
    } catch (err) {
      setIsCleaningSelectedGames(false)
    }
  }

  useEffect(() => {
    if (gameListSettingLoading || isCleaningSelectedGames || parsedParams.force === 'true') return
    const userSettingCompareGameList = gameListSetting as paramObj | undefined

    const hasParsed = Object.keys(parsedGamesWithMarketIso).length > 0
    const hasSaved = !!userSettingCompareGameList && Object.keys(userSettingCompareGameList).length > 0

    if (hasSaved && !hasParsed) {
      setSearchParams((currentParams) => ({ ...currentParams, gamesWithMarketIso: JSON.stringify(userSettingCompareGameList) }))
      return
    }

    if (hasParsed && !shallowCompare(parsedGamesWithMarketIso, userSettingCompareGameList)) {
      saveUserSetting({ settingKey: UserSettingKeys.compareGamesList, value: parsedGamesWithMarketIso })
    }
  }, [gameListSetting, gameListSettingLoading, isCleaningSelectedGames, parsedGamesWithMarketIso, parsedParams.force, saveUserSetting, setSearchParams])

  const defaultGames = useMemo(() => {
    if (!filteredGameAndAnalysisData) return []
    return filteredGameAndAnalysisData.map((data) => data.game)
  }, [filteredGameAndAnalysisData])

  const gameSearchDialogConfig = useMemo<GameSearchDialogConfig>((): GameSearchDialogConfig => {
    const tabs = [GameSearchDialogTab.Search]

    if (hasAccessToSoftLaunch) {
      tabs.push(GameSearchDialogTab.SoftLaunch)
    }

    tabs.push(GameSearchDialogTab.MyPortfolio)

    return {
      type: GameSearchDialogType.GameSelector,
      tabs: tabs,
      gameSelectorCounts: { max: maxGameCompare, min: minGameCompare },
    }
  }, [hasAccessToSoftLaunch])

  const onGameSearchSelected = (selectedGames?: Game[], selectedGamesWithMarketIso?: GameWithMarketIso) => {
    if (selectedGamesWithMarketIso) {
      setSearchParams((currentParams) => ({ ...currentParams, gamesWithMarketIso: JSON.stringify(selectedGamesWithMarketIso), force: undefined }))
    }

    if (selectedGames && selectedGames.length) {
      analyticsService.trackEvent('Compared games', {
        data: {
          gameIds: selectedGames.map((game) => game.id).join(', '),
          gameNames: selectedGames.map((game) => game.resolvedName).join(', '),
        },
        serviceToExclude: [AnalyticsProviders.hubspot, AnalyticsProviders.intercom],
      })
    }

    setGameSearchModalOpen(false)
  }

  const onGameRemoveClick = (removeGame: Game, marketIso?: string) => {
    const newParsedGamesWithMarketIso = { ...parsedGamesWithMarketIso }
    if (newParsedGamesWithMarketIso[removeGame.id].length > 1) {
      newParsedGamesWithMarketIso[removeGame.id] = newParsedGamesWithMarketIso[removeGame.id].filter((marketList) => marketList !== marketIso)
    } else {
      delete newParsedGamesWithMarketIso[removeGame.id]
    }
    if (!Object.keys(newParsedGamesWithMarketIso).length) {
      onClearAllGameClick()
      return
    }
    setSearchParams((currentParams) => ({ ...currentParams, gamesWithMarketIso: JSON.stringify(newParsedGamesWithMarketIso) }))
  }

  const renderTabView = () => {
    if (gameAndAnalysisDataLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      )
    }

    if (!filteredGameAndAnalysisData || !filteredGameAndAnalysisData.length || filteredGameAndAnalysisData.length < minGameCompare) {
      return (
        <GRBanner severity="guidance" icon={<Warning />}>
          <Typography ml={1}>{t('motivations:please_select_at_least_two_games')}</Typography>
        </GRBanner>
      )
    }

    switch (tabs[tabView].id) {
      case CompareGameTabSelection.FEATURES:
        return <FeatureTabView gamesAndAnalysis={filteredGameAndAnalysisData} gamesWithMarketIso={filteredGamesWithMarketIso} />
      case CompareGameTabSelection.MOTIVATION:
        return <MotivationsTabView gamesWithMarketIso={filteredGamesWithMarketIso} gamesAndAnalysis={filteredGameAndAnalysisData} />
      case CompareGameTabSelection.PERFORMANCE:
        return <PerformanceTabView gamesWithMarketIso={filteredGamesWithMarketIso} gamesAndAnalysis={filteredGameAndAnalysisData} />
      default:
        return null
    }
  }

  useEffect(() => {
    setTabView(defaultTabView)
  }, [defaultTabView, subpage])

  if (!subpage) {
    return <Navigate replace to={`/compare-games/${tabs[0].id}`} />
  }

  return hasCompareGamesAccess ? (
    <>
      <GameSearchDialog
        enableMarketSelector
        gameSearchDialogConfig={gameSearchDialogConfig}
        modalOpen={gameSearchModalOpen}
        onClose={onGameSearchSelected}
        defaultSelectedGames={defaultGames}
        defaultGamesWithMarketIso={filteredGamesWithMarketIso}
        onClearGameList={() => {
          setSearchParams({})
        }}
        onlyGamesWithPowerscore
      />

      <div className="CompareGamesPage">
        <Card sx={{ p: 2, borderBottomLeftRadius: 0, borderBottomRightRadius: 0, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
          <GameCards
            gamesAndAnalysis={filteredGameAndAnalysisData}
            addMoreGameClick={() => {
              setGameSearchModalOpen(true)
            }}
            onGameRemoveClick={onGameRemoveClick}
            onClearAllGameClick={onClearAllGameClick}
          />
        </Card>
        <Box sx={{ mt: 0 }}>
          <GRTabNavigation
            tabs={tabs}
            onChange={(tabIndex) => {
              const selectedTab = tabs[tabIndex]
              setTabView(tabIndex)
              setTimeout(() => {
                navigate(`/compare-games/${selectedTab.id}${location.search}`)
              }, 0)
            }}
            selectedTab={tabView}
            pagePrefix={'/compare-games'}
            sticky={false}
          />
        </Box>
        {renderTabView()}
      </div>
    </>
  ) : (
    <LockedFeature.Card lockedFeatureId={LockedFeatureId.CompareGames} />
  )
}

export default CompareGamesPage
